export interface Usage {
  inputTokens: number;
  outputTokens: number;
}

export enum MessageRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  DEVELOPER = 'developer',
}

export interface ChatMessageTextPart {
  type: 'text';
  text: string;
}

export interface ChatMessageUserImagePart {
  type: 'user_image';
  hashedFilename: string;
}

export interface ChatMessageDesignPart {
  type: 'design';
  hashedId: string;
}

export interface ChatMessageModelImagePart {
  type: 'image';
  hashedId: string;
}

export type ChatMessageUserAttachment = ChatMessageUserImagePart | ChatMessageDesignPart | ChatMessageModelImagePart;

export type ChatMessagePart = ChatMessageTextPart | ChatMessageUserAttachment;

export enum FailedMessageType {
  INTERNAL_ERROR = 'internal_error',
  STOPPED = 'stopped',
  TOO_MANY_REQUESTS = 'too_many_requests',
}

interface Message {
  role: MessageRole;
  parts: ChatMessagePart[];
}

export interface UserMessage extends Message {
  role: MessageRole.USER;
}

export interface ModelMessage extends Message {
  role: MessageRole.ASSISTANT;
  usage: Usage;
  isThinking: boolean;
}

export interface FailedMessage extends Message {
  role: MessageRole.DEVELOPER;
  failureType: FailedMessageType;
}

export interface DeveloperMessage extends Message {
  role: MessageRole.DEVELOPER;
}

export type ChatMessage = UserMessage | ModelMessage | FailedMessage | DeveloperMessage;

export interface MessagesResponse {
  idchat: string;
  history: ChatMessage[];
  activeMessageSessionUid?: string;
}

export interface AiChatWindowProps {
  chatId?: string;
  setChatId?: (chatId: string | undefined) => void;
  onChatStart?: VoidFunction;
  chatOnLoadParams?: ChatOnLoadParams;
  refreshChatHistory?: VoidFunction;
}

export interface StreamEvent {
  event: string;
  type: string;
  data?: string;
  message?: ChatMessage;
}

export interface ControlStreamEvent extends StreamEvent {
  event: 'control';
  type: 'chat_id' | 'chat_title' | 'thumbnail_url';
  data: string;
}

export interface ProgressStreamEvent extends StreamEvent {
  event: 'thinking';
  type: 'text';
  data: string;
}

export interface TextStreamEvent extends StreamEvent {
  event: 'chat' | 'response';
  type: 'text';
  message: ChatMessage;
}

export interface FailedMessageStreamEvent extends StreamEvent {
  event: 'failed_message';
  type: AiChatFailedMessageType;
  message: ChatMessage;
}

export enum AiChatFailedMessageType {
  INTERNAL_ERROR = 'internal_error',
  NSFW_CONTENT = 'nsfw_content',
  STOPPED = 'stopped',
  CREDITS_EXHAUSTED = 'credits_exhausted',
}

export interface ImageStreamEvent extends StreamEvent {
  event: 'image';
  type: 'generation';
  message: ChatMessage;
}

export interface CloseStreamEvent extends StreamEvent {
  event: 'close';
  type: 'control';
  data: '';
}

export type StreamDataChunk = ControlStreamEvent | ProgressStreamEvent | TextStreamEvent | FailedMessageStreamEvent | ImageStreamEvent | CloseStreamEvent;

export interface ChatOnLoadParams {
  userRequestParts: ChatMessagePart[];
}

export const SHELL_CONTAINER_QUERY = {
  small: {maxWidth: 600},
  medium: {minWidth: 601, maxWidth: 900},
  large: {minWidth: 901},
};

export interface AiChatStreamParams {
  chatId?: string;
  mode: ChatMode
  type: ChatType;
  userRequestParts: ChatMessagePart[];
  messageSessionUid?: string;
}

export enum ChatMode {
  CREATE = 'create',
  EDIT = 'edit',
}

export enum ChatType {
  DESIGN = 'design',
  ASSET = 'asset',
}
